import {useCallback, useEffect, useState} from 'react';

const INDEX_URL = '/articles/index.json';
const ITEM_URL = (id) => `/articles/items/${encodeURIComponent(id)}.json`;
const CACHE_KEY = 'lavita-articles-index-v1';

const normalize = (data) => {
    if (!Array.isArray(data?.articles)) {
        throw new Error('Article index did not include any articles');
    }
    return {
        categories: Array.isArray(data.categories) ? data.categories : [],
        articles: data.articles.map((a) => ({
            id: a.id,
            title: a.title || 'Untitled',
            category: a.category || 'Lavita',
            readingTime: a.readingTime || null,
            image: a.image || null,
            excerpt: a.excerpt || '',
        })),
    };
};

const readCache = () => {
    try {
        const raw = window.localStorage.getItem(CACHE_KEY);
        return raw ? normalize(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
};

const writeCache = (data) => {
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
        /* storage may be unavailable */
    }
};

// Fetch a single article's full content ({id, title, category, readingTime, html, text}).
export const fetchArticle = async (id, signal) => {
    const res = await fetch(ITEM_URL(id), {signal});
    if (!res.ok) {
        throw new Error(`Article request failed: ${res.status}`);
    }
    return res.json();
};

const useArticles = () => {
    const [categories, setCategories] = useState([]);
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(INDEX_URL, {signal});
            if (!res.ok) {
                throw new Error(`Index request failed: ${res.status}`);
            }
            const data = await res.json();
            const norm = normalize(data);
            writeCache(data);
            setCategories(norm.categories);
            setArticles(norm.articles);
        } catch (err) {
            if (err.name !== 'AbortError') {
                const cached = readCache();
                if (cached) {
                    setCategories(cached.categories);
                    setArticles(cached.articles);
                    setError('Showing the last available article list.');
                } else {
                    setError(err.message || 'Unable to load articles right now.');
                }
            }
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    const refresh = useCallback(() => load(), [load]);

    return {categories, articles, loading, error, refresh};
};

export default useArticles;
